import {
  useCallback,
  useState,
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
} from 'react';
import { t } from '@/lib/i18n';
import { agentSessionsService, type AgentSessionDTO } from '@/services/roros/agent-sessions.service';
import type { ChatMessage } from '../components/AgentChatPageParts';
import { getAgentSessionErrorMessage } from '../agentSessionErrorMessage';

type UsageDelta = {
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
};

type UseAgentSessionMessageStreamArgs = {
  activeSession: AgentSessionDTO | null;
  inputText: string;
  clearInput: () => void;
  inputRef: RefObject<HTMLInputElement | null>;
  setActiveSession: Dispatch<SetStateAction<AgentSessionDTO | null>>;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  replaceSession: (session: AgentSessionDTO) => void;
  addUsageFromChunk: (usage: UsageDelta) => void;
  syncCountersFromSession: (session: AgentSessionDTO) => void;
  abortRef: MutableRefObject<boolean>;
};

export function useAgentSessionMessageStream({
  activeSession,
  inputText,
  clearInput,
  inputRef,
  setActiveSession,
  setMessages,
  replaceSession,
  addUsageFromChunk,
  syncCountersFromSession,
  abortRef,
}: UseAgentSessionMessageStreamArgs) {
  const [isStreaming, setIsStreaming] = useState(false);

  const sendMessage = useCallback(async () => {
    if (!activeSession || !inputText.trim() || isStreaming) return;

    const userText = inputText.trim();
    clearInput();
    setIsStreaming(true);
    abortRef.current = false;

    // Add user message to local state
    const userId = `user-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: userId, role: 'user', content: userText },
    ]);

    // Placeholder for assistant response
    const assistantId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: 'assistant', content: '', pending: true },
    ]);

    let activeToolId: string | null = null;
    let assistantContent = '';

    try {
      const stream = agentSessionsService.streamMessage(activeSession.id, userText);
      for await (const chunk of stream) {
        if (abortRef.current) break;

        if (chunk.type === 'text_delta') {
          assistantContent += chunk.delta ?? chunk.text ?? '';
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: assistantContent, pending: true } : m,
            ),
          );
        } else if (chunk.type === 'tool_start') {
          activeToolId = `tool-${Date.now()}`;
          setMessages((prev) => [
            ...prev.filter((m) => m.id !== assistantId),
            { id: activeToolId!, role: 'tool', toolName: chunk.toolName, content: '', pending: true },
            { id: assistantId, role: 'assistant', content: assistantContent, pending: true },
          ]);
        } else if (chunk.type === 'tool_done' && activeToolId) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === activeToolId
                ? { ...m, content: chunk.toolResult ?? chunk.result ?? '', pending: false }
                : m,
            ),
          );
          activeToolId = null;
        } else if (chunk.type === 'usage') {
          // AC-AGS-10: update running token/cost counters from SSE usage events
          addUsageFromChunk(chunk);
        } else if (chunk.type === 'skill_error') {
          // AC-AGS-6: unknown skill → friendly error
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: `⚡ ${chunk.error ?? t('Skill not found')}`, pending: false }
                : m,
            ),
          );
          break;
        } else if (chunk.type === 'done') {
          break;
        } else if (chunk.type === 'error') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, content: `⚠ ${chunk.error ?? chunk.message ?? t('Error')}`, pending: false }
                : m,
            ),
          );
          break;
        }
      }
    } catch (err: unknown) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, content: `⚠ ${getAgentSessionErrorMessage(err) ?? t('Error')}`, pending: false }
            : m,
        ),
      );
    } finally {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, pending: false } : m,
        ),
      );
      setIsStreaming(false);
      inputRef.current?.focus();

      // Refresh session to pick up updated status/token totals
      if (activeSession) {
        try {
          const detail = await agentSessionsService.getSession(activeSession.id);
          setActiveSession(detail.session);
          replaceSession(detail.session);
          syncCountersFromSession(detail.session);
        } catch {
          // non-fatal
        }
      }
    }
  }, [
    activeSession,
    inputText,
    isStreaming,
    clearInput,
    abortRef,
    setMessages,
    inputRef,
    setActiveSession,
    replaceSession,
    addUsageFromChunk,
    syncCountersFromSession,
  ]);

  return {
    isStreaming,
    sendMessage,
  };
}
